import { t } from 'i18next';
import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { CgAddR, CgRemoveR } from 'react-icons/cg';
import { useFormikContext } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import useDebounce from '../../../../utils/useDebounceHook';
import { useSysExaminationType } from '../../../settings/sys-examination/hook';
import { optionsStyle } from '../composables';
import {
	getGeneral,
	getCvs,
	getRs,
	getCns,
	getPa,
	getEnt
} from '../../../../redux/visit';

const ExaminationDropdown = ({ options, value, onChange, onSearch }) => (
	<CreatableSelect
		menuPortalTarget={document.body}
		styles={optionsStyle}
		isClearable
		options={options}
		value={value}
		onChange={onChange}
		onInputChange={(inputValue, { action }) => {
			if (action === 'input-change') {
				onSearch(inputValue);
			}
		}}
	/>
);

const SysExaminationFields = () => {
	const [show, setShow] = useState(false);
	const { values, setFieldValue } = useFormikContext();
	const { general, cvs, rs, cns, pa, ent } = useSelector(state => state.visits);
	const dispatch = useDispatch();

	const typeList = useSysExaminationType({ t, general, cvs, rs, cns, pa, ent });

	const [optionsMap, setOptionsMap] = useState({});
	const [searchTerms, setSearchTerms] = useState({});
	const debouncedSearch = useDebounce(searchTerms, 500);

	const handleShow = () => setShow(prev => !prev);

	const handleSearchChange = (typeKey) => (value) => {
		setSearchTerms(prev => ({ ...prev, [typeKey]: value }));
	};

	const handleValueChange = (typeKey, idValue, nameValue = '') => {
		setFieldValue(`sysExamination.${typeKey}Id`, idValue || '');
		setFieldValue(`sysExamination.${typeKey}Name`, nameValue || '');
		//  setFieldValue(`sysExamination.${typeKey}`, {
		//    [`${typeKey}Id`]: idValue || '',
		//    [`${typeKey}Name`]: nameValue || ''
		//  });
	};

	const handleNewOption = (typeKey, label) => {
		const list = optionsMap[typeKey] || [];
		const isDuplicate =
      list.some(opt => opt.label.toLowerCase() === label.toLowerCase()) ||
      values.sysExamination?.[typeKey]?.[`${typeKey}Name`]?.toLowerCase() === label.toLowerCase();

		if (isDuplicate) return;

		const tempId = `temp-${Date.now()}`;
		const newOption = { label, value: tempId };
		setOptionsMap(prev => ({
			...prev,
			[typeKey]: [...(prev[typeKey] || []), newOption]
		}));
		handleValueChange(typeKey, tempId, label);
	};

	const mergeOptions = (typeKey, items) => {
		if (!items?.length) return;
		const newOptions = items.map(item => ({
			label: item.name,
			value: item.id
		}));
		setOptionsMap(prevMap => {
			const existing = prevMap[typeKey] || [];
			const existingIds = new Set(existing.map(opt => opt.value));
			const merged = [...existing];
			newOptions.forEach(opt => {
				if (!existingIds.has(opt.value)) {
					merged.push(opt);
				}
			});
			return { ...prevMap, [typeKey]: merged };
		});
	};

	// Merge options when redux store updates
	useEffect(() => {
		mergeOptions('general', general);
	}, [general]);
	useEffect(() => {
		mergeOptions('cvs', cvs);
	}, [cvs]);
	useEffect(() => {
		mergeOptions('rs', rs);
	}, [rs]);
	useEffect(() => {
		mergeOptions('cns', cns);
	}, [cns]);
	useEffect(() => {
		mergeOptions('pa', pa);
	}, [pa]);
	useEffect(() => {
		mergeOptions('ent', ent);
	}, [ent]);

	// Search handling (server-side)
	useEffect(() => {
		Object.entries(debouncedSearch).forEach(([typeKey, searchValue]) => {
			if (searchValue) {
				const params = { search: searchValue, per_page: 5, status: 'active' };
				switch (typeKey) {
					case 'general':
						dispatch(getGeneral(params));
						break;
					case 'cvs':
						dispatch(getCvs(params));
						break;
					case 'rs':
						dispatch(getRs(params));
						break;
					case 'cns':
						dispatch(getCns(params));
						break;
					case 'pa':
						dispatch(getPa(params));
						break;
					case 'ent':
						dispatch(getEnt(params));
						break;
					default:
						break;
				}
			}
		});
	}, [debouncedSearch, dispatch]);

	return (
		<Row className="my-2">
			<Card.Title onClick={handleShow} className='cursor-pointer'>
				{t('VISIT.SYSTEMIC_EXAMINATION')}
				{show ? <CgRemoveR className='ms-2' /> : <CgAddR className='ms-2' />}
			</Card.Title>

			{show && (
				<Row className="d-flex justify-content-between gy-2">
					{typeList.map((item, index) => {
						const fieldKey = item.value;
						const storedValue = values.sysExamination;
						const options = optionsMap[fieldKey] || [];

						const currentValue = storedValue?.[`${fieldKey}Id`]
							? options.find(opt => opt.value === storedValue[`${fieldKey}Id`]) || null
							: storedValue?.[`${fieldKey}Name`]
								? { value: storedValue[`${fieldKey}Name`], label: storedValue[`${fieldKey}Name`], __isNew__: true }
								: null;

						return (
							<Col md={4} key={index}>
								<Form.Label>{t(item.label)}</Form.Label>
								<ExaminationDropdown
									options={options}
									value={currentValue}
									onChange={(option) => {
										if (option?.__isNew__) {
											handleNewOption(fieldKey, option.label);
										} else {
											handleValueChange(fieldKey, option?.value || '', '');
										}
									}}
									onSearch={handleSearchChange(fieldKey)}
								/>
							</Col>
						);
					})}
				</Row>
			)}
		</Row>
	);
};

export default SysExaminationFields;
